import { Injectable } from '@angular/core';

export type MsgHookFn = (
  message: string
) => string | null | Promise<string | null>;

/**
 * Verarbeitet Nachrichten, bevor sie an den Bot gesendet werden (`RAGAI.BEFORE_SEND`).
 * @remarks
 * Registrierte Hooks laufen in Registrierungsreihenfolge über jede per `RAGAI.MSG.prompt()`/`userMessage()`
 * gesendete Nachricht. Ein Hook kann den Text verändern (String zurückgeben), das Senden komplett unterdrücken
 * (`null` zurückgeben) oder asynchron arbeiten (Promise zurückgeben).
 * Ruft ein Hook selbst wieder `RAGAI.MSG.prompt()`/`userMessage()` auf, würde das normalerweise einen neuen
 * Sendevorgang - und damit erneut alle Hooks - auslösen (Endlosschleife). Verschachtelte Aufrufe werden daher
 * per `isRunning`-Flag erkannt und unverändert (ohne erneute Hook-Verarbeitung) durchgereicht, inklusive einer
 * Warnung in der Browser-Konsole.
 */
@Injectable({
  providedIn: 'root'
})
export class MsgProcessingService {
  private hooks: MsgHookFn[] = [];
  private isRunning = false;

  /**
   * Registriert eine neue Nachrichten-Verarbeitungsfunktion.
   */
  public add(fn: MsgHookFn): void {
    this.hooks.push(fn);
  }

  /**
   * Führt alle registrierten Funktionen aus und gibt das modifizierte Ergebnis zurück.
   * Rückgabe `null` bedeutet: Verarbeitung abbrechen.
   *
   * Schützt vor Endlosrekursion: Löst ein Hook selbst wieder einen Sendevorgang aus
   * (z.B. `RAGAI.MSG.prompt()` / `.userMessage()`), würde `run()` erneut aufgerufen
   * werden, während der äußere Durchlauf noch läuft. In diesem Fall wird die Nachricht
   * unverändert durchgereicht, statt die Hooks erneut (und potenziell endlos) auszuführen.
   */
  public async run(message: string): Promise<string | null> {
    if (this.isRunning) {
      console.warn(
        '[RAGAI.BEFORE_SEND] Verschachtelter .run()-Aufruf erkannt (z.B. durch RAGAI.MSG.prompt() oder .userMessage() innerhalb eines BEFORE_SEND-Hooks). ' +
        'Um eine Endlosschleife zu vermeiden, wird diese Nachricht ohne erneute Hook-Verarbeitung weitergeleitet.'
      );
      return message;
    }

    this.isRunning = true;
    try {
      let current = message;

      for (const hook of this.hooks) {
        const result = await hook(current);
        if (result === null) return null; // Abbruch
        current = result;
      }

      return current;
    } finally {
      this.isRunning = false;
    }
  }

  /**
   * Entfernt alle registrierten Funktionen.
   */
  public clear(): void {
    this.hooks = [];
  }

  /**
   * Gibt die aktuell registrierten Funktionen zurück (z.B. zu Debug-Zwecken).
   */
  public list(): MsgHookFn[] {
    return [...this.hooks];
  }
}
